import { Search, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";

// Symbol search trigger + floating popover. Replaces the former inline input
// that only closed when the button was re-clicked: this popover dismisses on
// any outside click or Escape while leaving the active query in place (the
// caller owns the value, so the filter persists when the box is hidden).
export function SearchPopover({
  value,
  onChange,
}: {
  value: string;
  onChange: (next: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: PointerEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") setOpen(false);
    }
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const active = value.trim() !== "";

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-pressed={open || active}
        onClick={() => setOpen((prev) => !prev)}
        className={`inline-flex h-9 items-center gap-1.5 rounded-control border px-3 text-sm font-medium transition-colors ${
          active
            ? "border-primary text-primary"
            : "border-outline-variant text-on-surface-variant hover:bg-surface-container-high hover:text-on-surface"
        }`}
      >
        <Search aria-hidden="true" className="h-4 w-4" />
        Search
      </button>
      {open ? (
        <div
          role="dialog"
          aria-label="Search trades"
          className="absolute right-0 z-20 mt-2 w-64 rounded-control border border-outline-variant bg-surface-container p-2 shadow-lg"
        >
          <div className="flex items-center gap-1.5">
            <input
              type="text"
              autoFocus
              aria-label="Search by symbol"
              placeholder="Symbol…"
              value={value}
              onChange={(event) => onChange(event.target.value)}
              className="h-9 w-full rounded-control border border-outline-variant bg-surface px-3 text-sm text-on-surface focus:border-focus-ring focus:outline-none focus:ring-2 focus:ring-focus-ring"
            />
            {active ? (
              <button
                type="button"
                aria-label="Clear search"
                onClick={() => onChange("")}
                className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-control text-on-surface-variant hover:bg-surface-container-high hover:text-on-surface"
              >
                <X aria-hidden="true" className="h-4 w-4" />
              </button>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}
